import React, { useEffect, useState } from 'react';
import { Container, Card, Form, Row, Col, Button, Table, Badge, Modal } from 'react-bootstrap';
import { apiService, Boleta, DetalleBoleta } from '../services/api';
import { useParams } from 'react-router-dom';

const Boletas: React.FC = () => {
  const [boletas, setBoletas] = useState<Boleta[]>([]);
  const [loading, setLoading] = useState(false);
  const [fecha, setFecha] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  });
  const [totalMin, setTotalMin] = useState('');
  const [totalMax, setTotalMax] = useState('');
  const [detalle, setDetalle] = useState<DetalleBoleta[]>([]);
  const [showDetalle, setShowDetalle] = useState(false);
  const [boletaSel, setBoletaSel] = useState<Boleta | null>(null);
  const [totales, setTotales] = useState<{ subtotal: number; descuento_total: number; total_neto: number } | null>(null);

  const cargar = async () => {
    setLoading(true);
    try {
      let user = JSON.parse(localStorage.getItem('user') || '{}');
      let userId = user?.id_usuario || '';
      const response = await apiService.getBoletas({
        fecha_inicio: fecha,
        fecha_fin: fecha,
        per_page: 50,
        id_usuario: userId,
        total_min: totalMin ? Number(totalMin) : undefined,
        total_max: totalMax ? Number(totalMax) : undefined,
      });
      if (response.estado === 1 && response.registros) setBoletas(response.registros);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const verDetalle = async (id: number) => {
    try {
      const resp = await apiService.getBoleta(id);
      if (resp.estado === 1 && resp.data) setBoletaSel(resp.data as Boleta);
      const det = await apiService.getBoletaDetalles(id);
      if (det.estado === 1) {
        const detalles = (det as any).data as DetalleBoleta[];
        setDetalle(Array.isArray(detalles) ? detalles : []);
        const t = (det as any).totales;
        setTotales(t ? { subtotal: t.subtotal || 0, descuento_total: t.descuento_total || 0, total_neto: t.total_neto || 0 } : null);
      }
      setShowDetalle(true);
    } catch (e) { console.error(e); }
  };

  const params = useParams();
  useEffect(() => { cargar(); }, []);
  useEffect(() => {
    const id = Number(params.id);
    if (!Number.isNaN(id) && id > 0) {
      verDetalle(id);
    }
  }, [params.id]);

  return (
    <Container fluid>
      <Card className="shadow-sm border-0 mb-3">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0"><i className="fas fa-receipt me-2"></i> Boletas</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={(e) => { e.preventDefault(); cargar(); }}>
            <Row className="g-2">
              <Col md={3}>
                <Form.Control type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
              </Col>
              <Col md={2}>
                <Form.Control type="number" placeholder="Monto mín $" value={totalMin} onChange={(e) => setTotalMin(e.target.value)} min={0} />
              </Col>
              <Col md={2}>
                <Form.Control type="number" placeholder="Monto máx $" value={totalMax} onChange={(e) => setTotalMax(e.target.value)} min={0} />
              </Col>
              <Col md={3}>
                <div className="d-grid">
                  <Button type="submit" variant="primary" disabled={loading}>
                    <i className={`fas fa-search ${loading ? 'fa-spin' : ''} me-2`}></i>
                    Buscar
                  </Button>
                </div>
              </Col>
              <Col md={2}>
                <div className="d-grid">
                  <Button type="button" variant="outline-secondary" onClick={() => { setTotalMin(''); setTotalMax(''); setFecha(() => { const d = new Date(); const yyyy = d.getFullYear(); const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${yyyy}-${mm}-${dd}`; }); }}>
                    <i className="fas fa-times me-2"></i> Limpiar
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-light">
          <h6 className="mb-0">Últimas boletas del día ({boletas.length})</h6>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Boleta #</th>
                <th>Fecha/Hora</th>
                <th>Cliente</th>
                <th className="text-end">Total</th>
                <th>Estado</th>
                <th className="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {boletas.map(b => (
                <tr key={b.id_boleta}>
                  <td className="fw-semibold">#{b.id_boleta}</td>
                  <td>{new Date(b.fecha_ingreso_boleta).toLocaleString('es-CL')}</td>
                  <td>{b.nombre_cliente_boleta || 'Cliente'}</td>
                  <td className="text-end fw-bold text-primary">{new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(b.total_boleta || 0)}</td>
                  <td>
                    <Badge bg={b.id_estado_boleta === 1 ? 'success' : 'secondary'}>{b.id_estado_boleta === 1 ? 'Activa' : 'Anulada'}</Badge>
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-primary" onClick={() => verDetalle(b.id_boleta)}>
                      <i className="fas fa-eye me-1"></i> Ver
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showDetalle} onHide={() => setShowDetalle(false)} size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="fas fa-list me-2"></i>
            Detalle Boleta #{boletaSel?.id_boleta}
          </Modal.Title>
        </Modal.Header>
      <Modal.Body>
          <Row className="mb-3 g-3">
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h6 className="text-muted mb-2">Información de la Boleta</h6>
                  <div className="d-flex justify-content-between"><span>#</span><span className="fw-semibold">{boletaSel?.id_boleta}</span></div>
                  <div className="d-flex justify-content-between"><span>Fecha/Hora</span><span className="fw-semibold">{boletaSel ? new Date(boletaSel.fecha_ingreso_boleta).toLocaleString('es-CL') : ''}</span></div>
                  <div className="d-flex justify-content-between"><span>Cliente</span><span className="fw-semibold">{boletaSel?.nombre_cliente_boleta || 'Cliente'}</span></div>
                  <div className="d-flex justify-content-between"><span>Estado</span><span className="fw-semibold"><Badge bg={boletaSel?.id_estado_boleta === 1 ? 'success' : 'secondary'}>{boletaSel?.id_estado_boleta === 1 ? 'Activa' : 'Anulada'}</Badge></span></div>
                  <div className="d-flex justify-content-between"><span>Usuario</span><span className="fw-semibold">{boletaSel?.id_usuario}</span></div>
                  <div className="d-flex justify-content-between"><span>Caja</span><span className="fw-semibold">{boletaSel?.id_caja}</span></div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h6 className="text-muted mb-2">Pago</h6>
                  <div className="d-flex justify-content-between"><span>Medio</span><span className="fw-semibold">{boletaSel ? (boletaSel.id_medio_pago === 1 ? 'Efectivo' : boletaSel.id_medio_pago === 2 ? 'Tarjeta Débito' : boletaSel.id_medio_pago === 3 ? 'Tarjeta Crédito' : 'Transferencia') : ''}</span></div>
                  <div className="d-flex justify-content-between"><span>Total</span><span className="fw-bold text-primary">{new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(boletaSel?.total_boleta || 0)}</span></div>
                  <div className="d-flex justify-content-between"><span>Descuento</span><span className="fw-semibold">{new Intl.NumberFormat('es-CL').format(boletaSel?.descuento_boleta || 0)}</span></div>
                  <div className="d-flex justify-content-between"><span>Pago</span><span className="fw-semibold">{new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(boletaSel?.pago_boleta || 0)}</span></div>
                  <div className="d-flex justify-content-between"><span>Vuelto</span><span className="fw-semibold text-success">{new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(boletaSel?.vuelto_boleta || 0)}</span></div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {totales && (
            <Card className="border-0 shadow-sm mb-3"><Card.Body>
              <div className="row">
                <div className="col-md-4 d-flex justify-content-between"><span className="text-muted">Subtotal</span><span className="fw-semibold">${totales.subtotal.toLocaleString('es-CL')}</span></div>
                <div className="col-md-4 d-flex justify-content-between"><span className="text-muted">Descuento total</span><span className="fw-semibold">${totales.descuento_total.toLocaleString('es-CL')}</span></div>
                <div className="col-md-4 d-flex justify-content-between"><span className="text-muted">Total Neto</span><span className="fw-bold text-primary">${totales.total_neto.toLocaleString('es-CL')}</span></div>
              </div>
            </Card.Body></Card>
          )}

          <Table responsive hover size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Producto</th>
                <th className="text-center">Cantidad</th>
                <th className="text-end">Precio</th>
                <th className="text-end">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {detalle.map(d => {
                const sub = d.cantidad_producto * d.precio_producto;
                return (
                  <tr key={d.id_detalle_boleta}>
                    <td className="small">{d.producto?.nombre_producto || d.id_producto}</td>
                    <td className="text-center">{d.cantidad_producto}</td>
                    <td className="text-end">${d.precio_producto.toLocaleString('es-CL')}</td>
                    <td className="text-end">${sub.toLocaleString('es-CL')}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Boletas;
